import React from 'react';
import {
    OverlayDrawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Tooltip,
    Button,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import classNames from 'owa-classnames';
import { isCapabilityEnabled } from 'owa-capabilities';
import { bleedThroughCapability } from 'owa-capabilities-definitions/lib/bleedThroughCapability';
import { observer } from 'owa-mobx-react';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';
import { usePreviousValue } from 'owa-react-hooks/lib/usePreviousValue';
import { MAIN_APP_ID } from '../constants/constants';
import closeDrawerPane from '../orchestration/closeDrawerPane';
import isDrawerPaneVisible from '../selectors/isDrawerPaneVisible';
import type DrawerPaneProps from '../store/schema/DrawerPaneProps';
import { drawerContainer, drawerHeader, drawerBody, fullBleed } from './DrawerPane.scss';

const dismissIcon = <DismissRegular />;

export const DrawerPane: React.FunctionComponent<DrawerPaneProps> = observer(function DrawerPane({
    paneId,
    children,
    onDismiss,
    headerText,
    showCloseButton = true,
    className,
    headerClassName,
    titleClassName,
    bodyClassName,
    mountNode,
}: DrawerPaneProps) {
    const parentNodeRef = React.useRef<HTMLElement | null>(null);
    const targetWindow = (React.useContext(ProjectionContext) ?? window) as Window;
    const isOpen = isDrawerPaneVisible(paneId, targetWindow);
    const wasOpen = usePreviousValue(isOpen);

    // If the pane was open and is now closed, we call onDismiss callback
    if (wasOpen && !isOpen) {
        onDismiss?.();
    }

    // If the pane is open for the first time, we need to set the parentNodeRef
    if (isOpen && !parentNodeRef.current) {
        // If mountNode is not provided, try to mount to the mainApp element in the target window
        // Otherwise, renders on top of the whole page
        parentNodeRef.current = mountNode || targetWindow.document.getElementById(MAIN_APP_ID);
    }

    const parentNode = parentNodeRef.current;

    const handleCloseDrawerPane = React.useCallback(() => {
        closeDrawerPane(paneId, targetWindow);
    }, [paneId, targetWindow, onDismiss]);

    const dismissButton = React.useMemo(() => {
        if (showCloseButton) {
            return (
                <Tooltip content="Close" relationship="label">
                    <Button
                        appearance="subtle"
                        icon={dismissIcon}
                        onClick={handleCloseDrawerPane}
                    />
                </Tooltip>
            );
        }
        return null;
    }, [showCloseButton, handleCloseDrawerPane]);

    if (!isOpen) {
        return null;
    }

    return (
        <OverlayDrawer
            open={isOpen}
            mountNode={parentNode}
            modalType="non-modal"
            position="end"
            className={classNames(
                drawerContainer,
                isCapabilityEnabled(bleedThroughCapability) && fullBleed,
                className
            )}
            surfaceMotion={null} // Disabling for now the default motion because of this bug: https://github.com/microsoft/fluentui/issues/33583
        >
            <DrawerHeader className={classNames(drawerHeader, headerClassName)}>
                <DrawerHeaderTitle className={titleClassName} action={dismissButton}>
                    {headerText}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody className={classNames(drawerBody, bodyClassName)}>{children}</DrawerBody>
        </OverlayDrawer>
    );
},
'DrawerPane');
